import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Grid,
  Box,
  TextField,
  Typography,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Button,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { styled } from "@mui/system";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditIcon from '@mui/icons-material/Edit';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PreviewImage from "../imageUpload/previewImage";

const genderArray = ["Male", "Female", "Other"];

// Distant Vision options
const distantVisionOptions = ["6/6", "6/9", "6/12", "6/18", "6/24", "6/36", "6/60", "5/60", "4/60", "3/60", "2/60", "1/60", "FCCF", "CF", "HMCF", "HM", "PL+", "PL-", "NPL"];

// Near Vision options
const nearVisionOptions = ["N5", "N6", "N8", "N10", "N12", "N14", "N18", "N24", "N36"];

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    marginLeft: theme.spacing(2),
    paddingBottom: "100px",
    display: 'flex',
    flexDirection: 'column',
  },
}));

const StyledGridItem = styled(Grid)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(2),
  flexWrap: "nowrap",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    flexWrap: "wrap",
  },
  [theme.breakpoints.between("sm", "md")]: {
    flexDirection: "row",
    alignItems: "flex-start",
    flexWrap: "nowrap",
  },
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  minWidth: "200px",
  width: "200px",
  textAlign: "left",
  wordBreak: "break-word",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    width: "auto",
    minWidth: "auto",
    marginBottom: theme.spacing(1),
  },
  [theme.breakpoints.between("sm", "md")]: {
    minWidth: "180px",
    width: "180px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "300px",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "row",
    justifyContent: "flex-start",
    flexWrap: "nowrap",
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme, checked }) => ({
  border: checked ? "2px solid #0D1E83" : "1px solid rgba(0, 0, 0, 0.2)",
  width: "auto",
  margin: theme.spacing(0.5),
  borderRadius: "4px",
  padding: "4px 12px",
  whiteSpace: "nowrap",
  display: "inline-block",
  [theme.breakpoints.down("sm")]: {
    padding: "8px 16px",
    margin: "4px 4px",
  },
}));

const StyledRadio = styled(Radio)(({ theme }) => ({
  "&.Mui-checked": {
    color: "#0D1E83",
  },
  "&.MuiRadio-root": {
    borderRadius: "50%",
  },
  [theme.breakpoints.down("sm")]: {
    width: "24px",
    height: "24px",
  },
}));

const ReviewDetails = ({ formik, handleEdit, handleSubmit, handleSubmitReport, setAutoSubmitAfterInference }) => {
  const [isGeneratingInference, setIsGeneratingInference] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);

  const handleSubmitReportClick = async (e) => {
    // CRITICAL: Prevent default form submission behavior (especially important for Safari mobile)
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Validate images before submission
    if (!imagesAreValid) {
      alert('Images are missing or invalid. Please go back to the Image Upload step and upload images before submitting.');
      handleEdit(); // Navigate back to image upload step
      return;
    }

    // Prevent double submission
    if (isGeneratingInference) {
      return;
    }
    setIsGeneratingInference(true);
    
    // Set flag to prevent PatientInference from showing during submission
    if (setAutoSubmitAfterInference) {
      setAutoSubmitAfterInference(true);
    }
    
    // Safety timeout - always reset loading after 180 seconds
    const timeoutId = setTimeout(() => {
      setIsGeneratingInference(false);
      if (setAutoSubmitAfterInference) {
        setAutoSubmitAfterInference(false);
      }
      alert('Submission timed out. Please try again.');
    }, 180000);
    
    try {
      // First generate inference and get results directly
      const inferenceResults = await handleSubmit();
      
      // Now submit to backend with the inference results directly
      // This ensures we use the actual classification data, not waiting for formik/state updates
      if (handleSubmitReport) {
        // handleSubmitReport sets submittingReport=true on the wrapper (full-screen processing)
        await handleSubmitReport(inferenceResults);
        // After submission completes, reset the flag so PatientInference can show
        if (setAutoSubmitAfterInference) {
          setAutoSubmitAfterInference(false);
        }
        setIsGeneratingInference(false);
      }
    } catch (error) {
      if (setAutoSubmitAfterInference) {
        setAutoSubmitAfterInference(false);
      }
      setIsGeneratingInference(false);
      
      // Show more detailed error message
      console.error('Error during submission:', error);
      const errorMessage = error?.message || error?.toString() || 'Unknown error';
      
      // Provide user-friendly error messages
      let userMessage = 'An error occurred during submission. Please try again.';
      
      if (errorMessage.includes('Storage full') || errorMessage.includes('QuotaExceededError')) {
        userMessage = 'Storage is full. Please sync your data or clear browser storage, then try again.';
      } else if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        userMessage = 'Network error. Your data has been saved offline and will sync when online.';
      } else if (errorMessage.includes('patient') && errorMessage.includes('does not exist')) {
        userMessage = 'Patient data error. Please go back and re-enter patient information, then try again.';
      } else if (errorMessage.includes('Failed to save')) {
        userMessage = `Save failed: ${errorMessage}. Please try again.`;
      } else if (errorMessage !== 'Unknown error') {
        userMessage = `Error: ${errorMessage}`;
      }
      
      alert(userMessage);
    } finally {
      clearTimeout(timeoutId);
    }
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [leftPreviewOpen, setLeftPreviewOpen] = useState(false);
  const [rightPreviewOpen, setRightPreviewOpen] = useState(false);

  // Detect Safari browser - MUST be declared before useEffect that uses it
  const isSafari = useMemo(() => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }, []);

  // Get image data from formik - MUST be declared before useEffect that uses them
  const leftEye = formik.values.image_file?.left?.image;
  const rightEye = formik.values.image_file?.right?.image;
  
  // State for image sources - MUST be declared before useEffect that uses them
  const [leftImageSrc, setLeftImageSrc] = useState('');
  const [rightImageSrc, setRightImageSrc] = useState('');

  // Prevent page reload on review page - block keyboard shortcuts and show warning
  // Also monitor memory pressure on Safari mobile
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Your images will be lost if you reload this page. Are you sure you want to leave?';
      return e.returnValue;
    };

    // Block keyboard shortcuts for refresh (F5, Ctrl+R, Ctrl+Shift+R, etc.)
    const handleKeyDown = (e) => {
      // Block F5
      if (e.key === 'F5') {
        e.preventDefault();
        alert('Reload is disabled on the review page. Please use the navigation buttons to go back or submit the report.');
        return false;
      }
      
      // Block Ctrl+R or Cmd+R (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault();
        alert('Reload is disabled on the review page. Please use the navigation buttons to go back or submit the report.');
        return false;
      }
      
      // Block Ctrl+Shift+R or Cmd+Shift+R (hard refresh)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'R') {
        e.preventDefault();
        alert('Reload is disabled on the review page. Please use the navigation buttons to go back or submit the report.');
        return false;
      }
    };

    // Monitor memory pressure on Safari mobile
    // Safari iOS can reload pages due to memory pressure
    const handleMemoryWarning = () => {
      // Check if it's Safari on iOS device (not just mobile screen size)
      const isSafariIOS = isSafari && /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isSafariIOS) {
        // Proactively revoke blob URLs to free memory
        if (leftImageSrc && leftImageSrc.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(leftImageSrc);
          } catch (e) {
            // Ignore errors
          }
        }
        if (rightImageSrc && rightImageSrc.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(rightImageSrc);
          } catch (e) {
            // Ignore errors
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleKeyDown);
    
    // Listen for memory pressure events (if available)
    if ('memory' in performance) {
      const checkMemory = () => {
        const memory = performance.memory;
        if (memory) {
          const usedMB = memory.usedJSHeapSize / 1024 / 1024;
          const limitMB = memory.jsHeapSizeLimit / 1024 / 1024;
          const usagePercent = (usedMB / limitMB) * 100;
          
          // Log if memory usage is high (>80%)
          if (usagePercent > 80) {
            handleMemoryWarning();
          }
        }
      };
      
      // Check memory periodically (every 5 seconds)
      const memoryCheckInterval = setInterval(checkMemory, 5000);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('keydown', handleKeyDown);
        clearInterval(memoryCheckInterval);
      };
    } else {
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isSafari, isMobile, leftImageSrc, rightImageSrc]);

  // Helper function to check if image data is valid
  const isValidImageData = (imageData) => {
    if (!imageData) return false;
    if (imageData instanceof File || imageData instanceof Blob) return true;
    if (typeof imageData === 'string' && imageData.length > 0) return true;
    if (typeof imageData === 'object' && Object.keys(imageData).length > 0) {
      if (imageData.file && (imageData.file instanceof File || imageData.file instanceof Blob)) return true;
      // Restored draft / IndexedDB format: { data: ArrayBuffer, type, name }
      if (imageData.data && imageData.type) return true;
    }
    return false;
  };

  // Convert left image to URL with cleanup
  useEffect(() => {
    let blobUrl = null;

    const createImageSrc = async () => {
      if (!leftEye) {
        setLeftImageSrc('');
        return;
      }
      
      if (typeof leftEye === 'string') {
        setLeftImageSrc(leftEye);
        return;
      }
      
      // CRITICAL FIX: NEVER use data URLs on Safari mobile - they cause memory issues!
      // Data URLs store entire image in memory as base64, causing Safari to reload the page
      // Always use blob URLs which are more memory efficient
      
      if (leftEye && typeof leftEye === 'object' && leftEye.data && leftEye.type) {
        const blob = new Blob([leftEye.data], { type: leftEye.type });
        blobUrl = URL.createObjectURL(blob);
        setLeftImageSrc(blobUrl);
        return;
      }
      
      if (leftEye instanceof File || leftEye instanceof Blob) {
        blobUrl = URL.createObjectURL(leftEye);
        setLeftImageSrc(blobUrl);
        return;
      }
      
      if (leftEye && typeof leftEye === 'object' && leftEye.file) {
        if (leftEye.file instanceof File || leftEye.file instanceof Blob) {
          blobUrl = URL.createObjectURL(leftEye.file);
          setLeftImageSrc(blobUrl);
          return;
        }
      }
      
      setLeftImageSrc('');
    };

    createImageSrc();

    return () => {
      if (blobUrl) {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch (error) {
          console.warn('Error revoking left image blob URL:', error);
        }
      }
    };
  }, [leftEye]);

  // Convert right image to URL with cleanup
  useEffect(() => {
    let blobUrl = null;

    const createImageSrc = async () => {
      if (!rightEye) {
        setRightImageSrc('');
        return;
      }
      
      if (typeof rightEye === 'string') {
        setRightImageSrc(rightEye);
        return;
      }
      
      // CRITICAL FIX: NEVER use data URLs on Safari mobile - they cause memory issues!
      // Data URLs store entire image in memory as base64, causing Safari to reload the page
      // Always use blob URLs which are more memory efficient
      
      if (rightEye && typeof rightEye === 'object' && rightEye.data && rightEye.type) {
        const blob = new Blob([rightEye.data], { type: rightEye.type });
        blobUrl = URL.createObjectURL(blob);
        setRightImageSrc(blobUrl);
        return;
      }
      
      if (rightEye instanceof File || rightEye instanceof Blob) {
        blobUrl = URL.createObjectURL(rightEye);
        setRightImageSrc(blobUrl);
        return;
      }
      
      if (rightEye && typeof rightEye === 'object' && rightEye.file) {
        if (rightEye.file instanceof File || rightEye.file instanceof Blob) {
          blobUrl = URL.createObjectURL(rightEye.file);
          setRightImageSrc(blobUrl);
          return;
        }
      }
      
      setRightImageSrc('');
    };

    createImageSrc();

    return () => {
      if (blobUrl) {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch (error) {
          console.warn('Error revoking right image blob URL:', error);
        }
      }
    };
  }, [rightEye]);
  const selectedSingleImageReason = formik.values.image_file?.single_image_reason;
  const singleImageMode = selectedSingleImageReason && selectedSingleImageReason !== '';

  // Check if images are valid and exist
  const checkImagesValid = () => {
    if (singleImageMode) {
      // In single image mode, at least one image must be valid
      return isValidImageData(leftEye) || isValidImageData(rightEye);
    } else {
      // In dual image mode, both images must be valid
      return isValidImageData(leftEye) && isValidImageData(rightEye);
    }
  };

  const [showImageError, setShowImageError] = useState(false);
  const [imagesAreValid, setImagesAreValid] = useState(false);

  // Check images on mount and when formik values change
  useEffect(() => {
    const isValid = checkImagesValid();
    setImagesAreValid(isValid);
    if (!isValid) {
      setShowImageError(true);
    } else {
      setShowImageError(false);
    }
  }, [leftEye, rightEye, singleImageMode]);

  const options = [
    {label: "Patient is monocular", value: "Patient is monocular"},
    {label: "Unclear image due to the presence of other ophthalmic conditions", value: "Unclear image due to the presence of other ophthalmic conditions"},
  ];

  return (
    <StyledContainer maxWidth={false} sx={{ width: "100%", paddingX: isMobile ? 1 : 2 }}>
      {/* Patient Details Section - Exact same as newPatientForm but disabled */}
      <Box
        sx={{
          border: "1px solid rgba(0, 0, 0, 0.05)",
          borderRadius: "8px",
          padding: isMobile ? "16px" : "24px",
          marginBottom: 3,
          background: "#FFF",
          boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 0px 6px 0px rgba(0, 0, 0, 0.02)",
          width: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <Typography fontWeight={600} fontSize="18px" color="#0D1E83" sx={{ marginBottom: 2 }}>
          Patient Details
        </Typography>
        <Grid container spacing={2}>
        {/* Patient ID */}
        <StyledGridItem item xs={12}>
          <StyledLabel>Patient ID*</StyledLabel>
          <StyledTextField
            required
            placeholder="Eg. 1234ABCD33DD"
            variant="outlined"
            id="pat_info.otherId"
            name="pat_info.otherId"
            sx={{
              paddingLeft: "3px",
            }}
            value={formik.values.pat_info?.otherId || ""}
            disabled={true}
          />
        </StyledGridItem>

        {/* Gender */}
        <StyledGridItem item xs={12}>
          <StyledLabel>Gender*</StyledLabel>
          <StyledRadioGroup
            row={!isMobile}
            name="pat_info.gender"
            value={formik.values.pat_info?.gender || ""}
          >
            {genderArray.map((gender) => {
              const isSelected = formik.values.pat_info?.gender === gender.toLowerCase();
              return (
                <StyledFormControlLabel
                  key={gender}
                  value={gender.toLowerCase()}
                  control={
                    <StyledRadio
                      checked={isSelected}
                    />
                  }
                  label={gender}
                  disabled={true}
                  checked={isSelected}
                />
              );
            })}
          </StyledRadioGroup>
        </StyledGridItem>

        {/* Age */}
        <StyledGridItem item xs={12}>
          <StyledLabel>Age*</StyledLabel>
          <Box sx={{ maxWidth: "100px" }}>
            <StyledTextField
              required
              type="number"
              placeholder="XX"
              variant="outlined"
              id="pat_info.age"
              name="pat_info.age"
              sx={{
                paddingLeft: "3px",
              }}
              value={formik.values.pat_info?.age || ""}
              InputProps={{
                endAdornment: <InputAdornment position="end">Years</InputAdornment>,
              }}
              disabled={true}
            />
          </Box>
        </StyledGridItem>

        {/* Diabetes */}
        <StyledGridItem item xs={12}>
          <StyledLabel>Diabetes*</StyledLabel>
          <StyledRadioGroup
            row={!isMobile}
            name="pat_info.diabetes"
            value={formik.values.pat_info?.diabetes ?? ""}
          >
            {["Yes", "No", "Unknown"].map((option) => {
              const isSelected = formik.values.pat_info?.diabetes === option;
              return (
                <StyledFormControlLabel
                  key={option}
                  value={option}
                  control={<StyledRadio checked={isSelected} />}
                  label={option}
                  disabled={true}
                  checked={isSelected}
                />
              );
            })}
          </StyledRadioGroup>
        </StyledGridItem>

        {/* Duration Since Diabetes Diagnosed - Conditional */}
        {formik.values.pat_info?.diabetes === "Yes" && (
          <StyledGridItem item xs={12}>
            <StyledLabel>
              {isMobile ? "Duration Since Diabetes Diagnosed" : (
                <>Duration Since<br />Diabetes Diagnosed</>
              )}
            </StyledLabel>
            <Box sx={{ 
              display: "flex", 
              flexDirection: isMobile ? "row" : "row",
              gap: 2, 
              alignItems: isMobile ? "flex-start" : "flex-start",
              width: isMobile ? "100%" : "auto",
              flexWrap: isMobile ? "nowrap" : "wrap",
              marginLeft: isMobile ? 0 : 0
            }}>
              <Box sx={{ 
                display: "flex",
                flexDirection: "column",
                maxWidth: isMobile ? "48%" : "140px", 
                width: isMobile ? "48%" : "140px",
                flexShrink: 0
              }}>
                <StyledTextField
                  type="number"
                  placeholder="XX"
                  variant="outlined"
                  id="pat_info.diabetesDurationYears"
                  name="pat_info.diabetesDurationYears"
                  value={formik.values.pat_info?.diabetesDurationYears ?? ""}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">Years</InputAdornment>,
                  }}
                  sx={{
                    paddingLeft: "3px",
                    width: "100%",
                  }}
                  disabled={true}
                />
              </Box>
              <Box sx={{ 
                display: "flex",
                flexDirection: "column",
                maxWidth: isMobile ? "48%" : "140px", 
                width: isMobile ? "48%" : "140px",
                flexShrink: 0
              }}>
                <StyledTextField
                  type="number"
                  placeholder="XX"
                  variant="outlined"
                  id="pat_info.diabetesDurationMonths"
                  name="pat_info.diabetesDurationMonths"
                  value={formik.values.pat_info?.diabetesDurationMonths ?? ""}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">Months</InputAdornment>,
                  }}
                  sx={{
                    paddingLeft: "3px",
                    width: "100%",
                  }}
                  disabled={true}
                />
              </Box>
            </Box>
          </StyledGridItem>
        )}

        {/* Best Corrected Visual Acuity */}
        <StyledGridItem item xs={12}>
          <StyledLabel>
            {isMobile ? "Best Corrected Visual Acuity" : (
              <>Best Corrected<br />Visual Acuity</>
            )}
          </StyledLabel>
          <Box sx={{ 
            display: "flex", 
            flexDirection: "column",
            gap: 2,
            width: isMobile ? "100%" : "auto",
          }}>
            {/* Distant Vision Section */}
            <Box sx={{ 
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px" }}>
                Distant Vision
              </Typography>
              <Box sx={{ 
                display: "flex", 
                flexDirection: isMobile ? "row" : "row",
                gap: isMobile ? 1 : 2,
                alignItems: "flex-start",
                width: isMobile ? "100%" : "auto",
              }}>
                <Box sx={{ 
                  display: "flex",
                  flexDirection: "column",
                  width: isMobile ? "48%" : "140px",
                  minWidth: isMobile ? "120px" : "auto",
                  flexShrink: 0
                }}>
                  <Autocomplete
                    freeSolo
                    options={distantVisionOptions}
                    value={formik.values.pat_info.visualAcuity?.distant?.right ?? null}
                    disabled={true}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="__/__"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              <InputAdornment position="end" sx={{ marginLeft: "8px" }}>Right</InputAdornment>
                            </>
                          ),
                        }}
                        sx={{
                          paddingLeft: "3px",
                          width: "100%",
                        }}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ 
                  display: "flex",
                  flexDirection: "column",
                  width: isMobile ? "48%" : "140px",
                  minWidth: isMobile ? "120px" : "auto",
                  flexShrink: 0
                }}>
                  <Autocomplete
                    freeSolo
                    options={distantVisionOptions}
                    value={formik.values.pat_info.visualAcuity?.distant?.left ?? null}
                    disabled={true}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="__/__"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              <InputAdornment position="end" sx={{ marginLeft: "8px" }}>Left</InputAdornment>
                            </>
                          ),
                        }}
                        sx={{
                          paddingLeft: "3px",
                          width: "100%",
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>

            {/* Near Vision Section */}
            <Box sx={{ 
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px" }}>
                Near Vision
              </Typography>
              <Box sx={{ 
                display: "flex", 
                flexDirection: isMobile ? "row" : "row",
                gap: isMobile ? 1 : 2,
                alignItems: "flex-start",
                width: isMobile ? "100%" : "auto",
              }}>
                <Box sx={{ 
                  display: "flex",
                  flexDirection: "column",
                  width: isMobile ? "48%" : "140px",
                  minWidth: isMobile ? "120px" : "auto",
                  flexShrink: 0
                }}>
                  <Autocomplete
                    freeSolo
                    options={nearVisionOptions}
                    value={formik.values.pat_info.visualAcuity?.near?.right ?? null}
                    disabled={true}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="N__"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              <InputAdornment position="end" sx={{ marginLeft: "8px" }}>Right</InputAdornment>
                            </>
                          ),
                        }}
                        sx={{
                          paddingLeft: "3px",
                          width: "100%",
                        }}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ 
                  display: "flex",
                  flexDirection: "column",
                  width: isMobile ? "48%" : "140px",
                  minWidth: isMobile ? "120px" : "auto",
                  flexShrink: 0
                }}>
                  <Autocomplete
                    freeSolo
                    options={nearVisionOptions}
                    value={formik.values.pat_info.visualAcuity?.near?.left ?? null}
                    disabled={true}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="N__"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              <InputAdornment position="end" sx={{ marginLeft: "8px" }}>Left</InputAdornment>
                            </>
                          ),
                        }}
                        sx={{
                          paddingLeft: "3px",
                          width: "100%",
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </StyledGridItem>
      </Grid>
      </Box>

      {/* Image Upload Section */}
      <Box
        sx={{
          border: "1px solid rgba(0, 0, 0, 0.05)",
          borderRadius: "8px",
          padding: isMobile ? "16px" : "24px",
          marginBottom: 3,
          background: "#FFF",
          boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 0px 6px 0px rgba(0, 0, 0, 0.02)",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Typography fontWeight={600} fontSize="18px" color="#0D1E83" sx={{ marginBottom: 2 }}>
          Image Upload
        </Typography>
        <Box sx={{
          width: "100%", 
          display: "flex", 
          justifyContent: "space-evenly", 
          paddingX: isMobile ? "0px" : "16px", 
          height: isMobile ? "auto" : "", 
          overflowX: isMobile ? "auto" : "visible",
          boxSizing: "border-box", 
          gap: isMobile ? "5px" : "10px"
        }}>
        {/* Right Eye */}
        <Box sx={{
          width: isMobile ? "45%" : "24vw",
          maxWidth: isMobile ? "45%" : "24vw",
          flexShrink: 0,
          boxSizing: "border-box"
        }}>
          <Box sx={{border: "1px solid rgba(0, 0, 0, 0.05)", borderRadius: "8px", display: isMobile?"flex":"", flexDirection:isMobile?"column":"", gap: isMobile? "5px":""}}>
            <Container sx={{background: "#EAF4FA", padding: "1% 0"}}>
              <Typography display="flex" alignItems="center" color="#0D1E83" fontSize="2.3vh" fontWeight="700" justifyContent="center">
                R
                <VisibilityOutlinedIcon sx={{height: "3em", width: "3em"}}/>
              </Typography>
              <Typography display="flex" justifyContent="center" color="#949494" fontWeight="400">
                Right Eye
              </Typography>
            </Container>
            <Container sx={{ minHeight: isMobile?"25vh":"32vh", padding: isMobile?"0px":"12px"}}>
              <Box sx={{
                border: "1px dashed #B8B3B4", 
                borderRadius: "4px", 
                padding: isMobile?"8px 0px":"5px", 
                height: isMobile?"25vh":"32vh", 
                background:isMobile && !rightEye?"#F0EEEE":"",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: isMobile?"25vh":"32vh"
              }}>
                {isValidImageData(rightEye) ? (
                  <>
                    <img 
                      src={rightImageSrc} 
                      alt="Right Eye" 
                      style={{ 
                        borderRadius: "4px", 
                        width: "100%", 
                        height: "100%", 
                        objectFit: "contain"
                      }}
                    />
                    <Box
                      onClick={() => setRightPreviewOpen(true)}
                      sx={{
                        position: "absolute",
                        bottom: "8px",
                        left: "8px",
                        cursor: "pointer",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        borderRadius: "4px",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <OpenInFullIcon sx={{ fontSize: "16px", color: "#0D1E83" }} />
                    </Box>
                  </>
                ) : (
                  <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    backgroundColor: "#F0EEEE",
                    width: "100%",
                    height: "100%",
                    borderRadius: "4px"
                  }}>
                    <Box sx={{
                      width: "60px",
                      height: "60px",
                      border: "2px solid #ccc",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#fff"
                    }}>
                      <Box sx={{
                        width: "40px",
                        height: "40px",
                        border: "2px solid #ccc",
                        borderRadius: "4px",
                        position: "relative"
                      }}>
                        <Box sx={{
                          position: "absolute",
                          top: "-2px",
                          left: "-2px",
                          width: "44px",
                          height: "2px",
                          backgroundColor: "#ccc",
                          transform: "rotate(45deg)",
                          transformOrigin: "center"
                        }} />
                      </Box>
                    </Box>
                    <Typography sx={{
                      fontSize: "16px",
                      color: "#999",
                      fontWeight: "500"
                    }}>
                      Image not available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Container>
          </Box>
        </Box>

        {/* Left Eye */}
        <Box sx={{
          width: isMobile ? "45%" : "24vw",
          maxWidth: isMobile ? "45%" : "24vw",
          flexShrink: 0,
          boxSizing: "border-box"
        }}>
          <Box sx={{border: "1px solid rgba(0, 0, 0, 0.05)", borderRadius: "8px", display: isMobile?"flex":"", flexDirection:isMobile?"column":"", gap: isMobile? "5px":""}}>
            <Container sx={{background: "#EAF4FA", padding: "1% 0"}}>
              <Typography display="flex" alignItems="center" color="#0D1E83" fontSize="2.3vh" fontWeight="700" justifyContent="center">
                <VisibilityOutlinedIcon sx={{height: "3em", width: "3em"}}/>
                L
              </Typography>
              <Typography display="flex" justifyContent="center" color="#949494" fontWeight="400">
                Left Eye
              </Typography>
            </Container>
            <Container sx={{ minHeight: isMobile?"25vh":"32vh", padding: isMobile?"0px":"12px"}}>
              <Box sx={{
                border: "1px dashed #B8B3B4", 
                borderRadius: "4px", 
                padding: isMobile?"8px 0px":"5px", 
                height: isMobile?"25vh":"32vh", 
                background:isMobile && !leftEye?"#F0EEEE":"",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: isMobile?"25vh":"32vh"
              }}>
                {isValidImageData(leftEye) ? (
                  <>
                    <img 
                      src={leftImageSrc} 
                      alt="Left Eye" 
                      style={{ 
                        borderRadius: "4px", 
                        width: "100%", 
                        height: "100%", 
                        objectFit: "contain"
                      }}
                    />
                    <Box
                      onClick={() => setLeftPreviewOpen(true)}
                      sx={{
                        position: "absolute",
                        bottom: "8px",
                        left: "8px",
                        cursor: "pointer",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        borderRadius: "4px",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <OpenInFullIcon sx={{ fontSize: "16px", color: "#0D1E83" }} />
                    </Box>
                  </>
                ) : (
                  <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    backgroundColor: "#F0EEEE",
                    width: "100%",
                    height: "100%",
                    borderRadius: "4px"
                  }}>
                    <Box sx={{
                      width: "60px",
                      height: "60px",
                      border: "2px solid #ccc",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#fff"
                    }}>
                      <Box sx={{
                        width: "40px",
                        height: "40px",
                        border: "2px solid #ccc",
                        borderRadius: "4px",
                        position: "relative"
                      }}>
                        <Box sx={{
                          position: "absolute",
                          top: "-2px",
                          left: "-2px",
                          width: "44px",
                          height: "2px",
                          backgroundColor: "#ccc",
                          transform: "rotate(45deg)",
                          transformOrigin: "center"
                        }} />
                      </Box>
                    </Box>
                    <Typography sx={{
                      fontSize: "16px",
                      color: "#999",
                      fontWeight: "500"
                    }}>
                      Image not available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Container>
          </Box>
        </Box>
      </Box>

      {/* Checkbox for mobile */}
      {singleImageMode && isMobile && (
        <Box sx={{ 
          padding: '6px 16px', 
          display: 'flex', 
          justifyContent: 'flex-start',
          width: '100%',
          backgroundColor: '#fff',
          borderTop: '1px solid #f0f0f0',
          marginTop: 2
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={true}
                disabled
                sx={{
                  color: '#0D1E83',
                  '&.Mui-checked': {
                    color: '#0D1E83',
                  },
                }}
              />
            }
            label="Only one eye image available"
            sx={{
              color: '#0D1E83',
              fontSize: '14px',
              fontWeight: 500,
            }}
          />
        </Box>
      )}

      {/* Checkbox for desktop */}
      {singleImageMode && !isMobile && (
        <Box sx={{ 
          padding: '12px 0', 
          display: 'flex', 
          justifyContent: 'flex-start',
          width: '100%',
          marginTop: 2
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={true}
                disabled
                sx={{
                  color: '#0D1E83',
                  '&.Mui-checked': {
                    color: '#0D1E83',
                  },
                }}
              />
            }
            label="Only one eye image available"
            sx={{
              color: '#0D1E83',
              fontSize: '14px',
              fontWeight: 500,
            }}
          />
        </Box>
      )}

      {/* Inline Radio Options for Single Image Mode */}
      {singleImageMode && (
        <Box sx={{
          padding: '16px',
          backgroundColor: '#EAF4FA',
          borderTop: '1px solid #f0f0f0',
          marginTop: isMobile ? 0 : '8px',
          borderRadius: '8px'
        }}>
          <RadioGroup value={selectedSingleImageReason}>
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio
                    checked={selectedSingleImageReason === option.value}
                    disabled
                    sx={{
                      color: '#0D1E83',
                      '&.Mui-checked': { color: '#0D1E83' }
                    }}
                  />
                }
                label={option.label}
                sx={{
                  mb: 1.5,
                  '& .MuiFormControlLabel-label': {
                    color: selectedSingleImageReason === option.value ? '#0D1E83' : '#666666',
                    fontWeight: selectedSingleImageReason === option.value ? 500 : 400,
                    fontSize: '16px'
                  }
                }}
              />
            ))}
          </RadioGroup>
        </Box>
      )}
      </Box>

      {/* Error message if images are missing */}
      {showImageError && (
        <Box sx={{
          padding: '16px',
          backgroundColor: '#ffebee',
          border: '2px solid #f44336',
          borderRadius: '8px',
          marginTop: 3,
          marginBottom: 2
        }}>
          <Typography sx={{
            color: '#d32f2f',
            fontWeight: 600,
            fontSize: '16px',
            marginBottom: 1
          }}>
            ⚠️ Images Missing
          </Typography>
          <Typography sx={{
            color: '#c62828',
            fontSize: '14px'
          }}>
            Images have been lost (possibly due to page reload). Please click "Edit" to go back to the Image Upload step and upload images again before submitting.
          </Typography>
        </Box>
      )}

      {/* Buttons */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2,
        marginTop: 3,
        paddingBottom: isMobile ? '20px' : 0
      }}>
        <Button
          onClick={handleEdit}
          startIcon={<EditIcon />}
          sx={{
            color: "#0D1E83",
            textTransform: "none",
            border: "1px solid #0D1E83",
            borderRadius: "8px",
            px: 3,
            py: 1.5,
            fontSize: "16px",
            fontWeight: 400
          }}
        >
          Edit
        </Button>
        <Button
          variant="contained"
          onClick={() => setSubmitConfirmOpen(true)}
          disabled={!imagesAreValid || isGeneratingInference}
          endIcon={<ArrowForwardIcon />}
          sx={{
            borderRadius: "8px",
            fontWeight: 400,
            fontSize: "16px",
            py: 1.5,
            px: 3,
            background: imagesAreValid 
              ? 'linear-gradient(270deg, #051572 7.32%, #0820B4 92.68%)'
              : '#cccccc',
            border: '1px solid #7D797B80',
            textTransform: "none",
            cursor: imagesAreValid ? 'pointer' : 'not-allowed',
            '&:disabled': {
              background: '#cccccc',
              color: '#666666'
            }
          }}
        >
          {isGeneratingInference ? 'Submitting...' : 'Submit'}
        </Button>
      </Box>

      {/* Submit Patient Details confirmation dialog */}
      <Dialog
        open={submitConfirmOpen}
        onClose={() => setSubmitConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: 2 }}>
          <Typography fontWeight={600} fontSize="20px" color="#2D363D">
            Submit Patient Details
          </Typography>
          <Button
            endIcon={<CloseIcon />}
            onClick={() => setSubmitConfirmOpen(false)}
            sx={{ color: "#0D1E83", textTransform: "none" }}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ padding: "0 24px 8px" }}>
          <Typography fontSize="16px" fontWeight={400} color="#2D363D">
            Are you sure you want to submit?
          </Typography>
          <Typography fontSize="16px" fontWeight={400} color="#2D363D" sx={{ mt: 1 }}>
            Once submitted, patient details cannot be edited.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", gap: 2, justifyContent: "center", flexWrap: "nowrap" }}>
          <Button
            variant="outlined"
            onClick={() => setSubmitConfirmOpen(false)}
            sx={{
              border: "1px solid #0D1E83",
              color: "#0D1E83",
              borderRadius: "8px",
              textTransform: "none",
              flex: 1,
              maxWidth: 160,
              py: 1.25,
            }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSubmitConfirmOpen(false);
              handleSubmitReportClick();
            }}
            sx={{
              background: "linear-gradient(270deg, #051572 7.32%, #0820B4 92.68%)",
              borderRadius: "8px",
              color: "#FFFFFF",
              textTransform: "none",
              flex: 1,
              maxWidth: 160,
              py: 1.25,
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Image Dialogs */}
      {rightPreviewOpen && (
        <PreviewImage 
          open={rightPreviewOpen} 
          handlePreviewClose={() => setRightPreviewOpen(false)} 
          files={rightEye} 
          eye="right"
        />
      )}
      {leftPreviewOpen && (
        <PreviewImage 
          open={leftPreviewOpen} 
          handlePreviewClose={() => setLeftPreviewOpen(false)} 
          files={leftEye} 
          eye="left"
        />
      )}

      {/* Processing Screen for Inference Generation */}
    </StyledContainer>
  );
};

export default ReviewDetails;
